import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { hasPermissionAixcDeploy } from 'shared/lib/hasPermission';
import { PAGE_STATE_TYPES_NEED_APPROVAL } from 'shared/constants';
import { fetchLogout } from '~/app/actions';
import { S, A, DARK } from '~/app/ui/tokens';
import { tr } from '~/app/ui/lang';
import Sidebar from './Sidebar';
import { navGroups } from './navModel';

// Same name as the old top navigation's query: other screens refetch 'getNavData' after changes.
const NAV_DATA = gql`
  query getNavData {
    pendingDeployment {
      added { _id }
      deleted { _id }
      removed { _id }
    }
    pages: pagesPendingDeployment {
      state
      canUserApprove
    }
  }
`;

type RoleRef = { roleType?: string; category?: string };
type User = {
  displayName?: string;
  username?: string;
  isSuperAdmin?: boolean;
  isActive?: boolean;
  roles?: { categories?: RoleRef[]; others?: RoleRef[] };
};

const NEED_APPROVAL: string[] = Object.values(PAGE_STATE_TYPES_NEED_APPROVAL);
const COLLAPSED_KEY = 'gx-nav-collapsed';
const MOBILE = '(max-width: 900px)';

const readCollapsed = () => {
  try { return window.localStorage.getItem(COLLAPSED_KEY) === '1'; } catch (e) { return false; }
};

export function roleLabel(user: User, isAixcDeployer: boolean): string {
  if (user.isSuperAdmin) return tr('Super admin', '超级管理员');
  if (isAixcDeployer) return tr('AIXC deployer', 'AIXC 发布者');
  const roles = [...(user.roles?.categories || []), ...(user.roles?.others || [])];
  if (roles.some(r => r.roleType === 'APPROVER')) return tr('Approver', '审批者');
  return tr('Editor', '编辑');
}

const useStyles = createUseStyles({
  shell: {
    display: 'flex',
    height: '100%',
    width: '100%',
    background: A.surface,
    fontFamily: S.ui
  },
  side: { flexShrink: 0, height: '100%' },
  main: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'relative',
    background: A.surface
  },
  mobileBar: {
    display: 'none',
    alignItems: 'center',
    gap: 8,
    height: 52,
    padding: '0 8px',
    borderBottom: `1px solid ${S.line}`,
    background: S.chrome,
    position: 'sticky',
    top: 0,
    zIndex: 20
  },
  mobileTitle: { fontSize: 16, fontWeight: 600, flex: 1 },
  menuButton: {
    all: 'unset',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 8,
    color: S.fg,
    cursor: 'pointer',
    '&:hover': { background: S.chromeHover },
    '&:focus-visible': A.focus
  },
  scrim: {
    position: 'fixed',
    inset: 0,
    zIndex: 1900,
    background: 'rgba(23, 26, 32, 0.32)',
    animation: '$gxFade 140ms ease-out',
    [DARK]: { background: S.scrim }
  },
  drawer: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: 1901,
    boxShadow: A.pop,
    animation: '$gxSlide 180ms ease-out'
  },
  '@keyframes gxFade': { from: { opacity: 0 }, to: { opacity: 1 } },
  '@keyframes gxSlide': { from: { transform: 'translateX(-100%)' }, to: { transform: 'none' } },
  '@media (max-width: 900px)': {
    side: { display: 'none' },
    mobileBar: { display: 'flex' }
  }
});

/**
 * The frame around every admin screen except the full-window ones: the
 * sidebar on the left (a drawer on small screens) and the screen beside it.
 */
const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const c = useStyles();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { data } = useQuery(NAV_DATA, { fetchPolicy: 'cache-and-network' });
  const user: User = useSelector((state: { app: { user: User } }) => state.app.user) || {};
  // Requests waiting for this user (same rule as the approvals selector: a state that needs approval, and theirs to approve).
  const pendingMine = (data?.pages || []).filter((p: { state?: string; canUserApprove?: boolean }) => NEED_APPROVAL.includes(p.state || '') && p.canUserApprove).length;
  const isSuperAdmin = !!user.isSuperAdmin;
  const isAixcDeployer = hasPermissionAixcDeploy(user) && !isSuperAdmin;

  const pending = data?.pendingDeployment;
  const deployCount = pending ? (pending.added?.length || 0) + (pending.deleted?.length || 0) + (pending.removed?.length || 0) : 0;
  const groups = navGroups({ isSuperAdmin, isAixcDeployer }, { approvals: pendingMine, deploy: deployCount });
  const role = roleLabel(user, isAixcDeployer);

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleCollapsed = () => setCollapsed(v => {
    try { window.localStorage.setItem(COLLAPSED_KEY, v ? '0' : '1'); } catch (e) { /* the choice just isn't remembered */ }
    return !v;
  });

  // The drawer closes when you go somewhere, press Escape, or the window grows past the mobile size.
  useEffect(() => { setDrawerOpen(false); }, [pathname]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    const mq = window.matchMedia(MOBILE);
    const onMq = () => { if (!mq.matches) setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    mq.addListener(onMq);
    return () => { window.removeEventListener('keydown', onKey); mq.removeListener(onMq); };
  }, [drawerOpen]);

  const logout = () => dispatch(fetchLogout());
  const sidebarProps = { groups, pathname, user, roleLabel: role, onLogout: logout };

  return (
    <div className={c.shell}>
      <div className={c.side}>
        <Sidebar {...sidebarProps} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
      </div>
      <div className={c.main} data-testid="app-main">
        <div className={c.mobileBar}>
          <button type="button" className={c.menuButton} aria-label="Open navigation" aria-expanded={drawerOpen} onClick={() => setDrawerOpen(true)} data-testid="nav-open">
            <i className="material-icons" aria-hidden>menu</i>
          </button>
          <span className={c.mobileTitle}>Genesis</span>
        </div>
        {children}
      </div>
      {drawerOpen && (
        <>
          <div className={c.scrim} onClick={() => setDrawerOpen(false)} aria-hidden />
          <div className={c.drawer} role="dialog" aria-modal="true" aria-label="Navigation">
            <Sidebar {...sidebarProps} collapsed={false} onToggleCollapsed={toggleCollapsed} drawer onNavigate={() => setDrawerOpen(false)} />
          </div>
        </>
      )}
    </div>
  );
};

export default AppShell;
